/**
 * Authentication utilities for handling auth state and redirects
 */

import { ApiError } from '../api/user-portal-api-generated';
import { SessionService, SamlService } from '../api/user-portal-service';
import { logError } from '../utils/error-logger';
import { isPublicPath, ROUTES } from './auth-routes';
import { getTenantFromPath, buildTenantPath } from './tenant-utils';

// Constants
const POST_AUTH_REDIRECT_KEY = 'post_auth_redirect';
const TENANT_NOT_FOUND_ERROR_CODE = 'TENANT_NOT_FOUND';
const HTTP_NOT_FOUND = 404;

/**
 * Backend tenant middlewares answer 404 with this error code when the tenant
 * slug doesn't exist — retrying SAML would loop, route to tenant selection.
 */
export function isTenantNotFoundError(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.status === HTTP_NOT_FOUND &&
    (error.body as { error_code?: string } | null)?.error_code ===
      TENANT_NOT_FOUND_ERROR_CODE
  );
}

function getAuthFailureFallbackUrl(error: unknown): string {
  const tenant = getTenantFromPath();

  if (!tenant || isTenantNotFoundError(error)) {
    return ROUTES.TENANT_SELECTION;
  }

  return buildTenantPath(tenant, ROUTES.AUTH_ERROR);
}

/**
 * Handle 401 unauthorized errors by firing the SAML SP-initiated flow.
 * Stores the current pathname so the user returns to it post-auth.
 */
export function handleUnauthorized(): void {
  sessionStorage.clear();
  storePostAuthRedirect(window.location.pathname);
  initiateSamlAuth().catch(error => {
    logError(error, 'auth:handleUnauthorized');
    window.location.href = getAuthFailureFallbackUrl(error);
  });
}
/**
 * Check if user is authenticated
 */
export async function isAuthenticated(): Promise<boolean> {
  try {
    return await SessionService.authenticated();
  } catch (error) {
    logError(error, 'auth:isAuthenticated');

    return false;
  }
}

/**
 * Logout the user with SAML logout flow
 * 1. Clear session storage
 * 2. Call SAML logout endpoint
 * 3. Redirect to IdP SLO using the redirect_url from response
 */
export async function logout(): Promise<void> {
  try {
    // Step 1: Clear local session storage
    sessionStorage.clear();

    // Step 2: Call the backend SAML logout endpoint
    const response = await SamlService.samlLogout();

    // Step 3: Redirect to the SAML logout URL (IdP SLO)
    const { redirect_url } = response;
    window.location.href = redirect_url;
  } catch (error) {
    logError(error, 'auth:logout');
    sessionStorage.clear();
    window.location.href = getAuthFailureFallbackUrl(error);
  }
}

// User ID is now handled server-side via session context
// No need for client-side user ID management

/**
 * Store a pathname for post-auth redirect.
 * The caller (authLoader) passes `request.url` pathname so we capture the
 * correct destination even during SPA navigation (before `window.location`
 * has been updated by React Router).
 */
export function storePostAuthRedirect(path: string): void {
  sessionStorage.setItem(POST_AUTH_REDIRECT_KEY, path);
}

export async function initiateSamlAuth(): Promise<void> {
  const response = await SamlService.initSamlAuth();
  window.location.replace(response.redirect_url);
}

/**
 * Get and clear the stored post-auth redirect path.
 * The stored value is already a pathname (set by {@link storePostAuthRedirect}).
 * @returns The stored redirect path or null if none exists / is unsafe
 */
export function getPostAuthRedirect(): string | null {
  const storedPath = sessionStorage.getItem(POST_AUTH_REDIRECT_KEY);

  if (!storedPath) {
    return null;
  }

  // Clear it immediately to prevent reuse
  sessionStorage.removeItem(POST_AUTH_REDIRECT_KEY);

  // Security: Validate the redirect path
  // 1. Ensure it's a relative path (starts with /)
  // 2. Prevent open redirects by checking it's not an absolute URL
  // 3. Skip auth-related paths to prevent redirect loops
  // 4. Reject cross-tenant redirects (stale redirect from a different tenant)
  const currentTenant = getTenantFromPath();

  if (
    !storedPath.startsWith('/') ||
    storedPath.startsWith('//') ||
    isPublicPath(storedPath) ||
    storedPath.includes('/idp/') ||
    (currentTenant && !storedPath.startsWith(`/${currentTenant}/`))
  ) {
    return null;
  }

  return storedPath;
}
